import React, { useState, useMemo } from 'react';
import { 
  Search, 
  Plus, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  LayoutGrid,
  List as ListIcon,
  Columns,
  User as UserIcon,
  Tag,
  Trash2,
  Edit
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Task, 
  TaskStatus, 
  Priority, 
  Project,
  User
} from './types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TasksPageProps {
  tasks: Task[];
  projects: Project[];
  users: User[];
  onAddTask: (task: Omit<Task, 'id' | 'createdAt'>) => void;
  onUpdateTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
}

const Badge = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
    {children}
  </span>
);

const Card = ({ children, className = "", onClick }: { children: React.ReactNode, className?: string, onClick?: () => void }) => (
  <div onClick={onClick} className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>
    {children}
  </div>
);

export function TasksPage({ tasks, projects, users, onAddTask, onUpdateTask, onDeleteTask }: TasksPageProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'kanban'>('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'ALL'>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<Priority | 'ALL'>('ALL');
  const [projectFilter, setProjectFilter] = useState<string | 'ALL'>('ALL');
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          task.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || task.status === statusFilter;
      const matchesPriority = priorityFilter === 'ALL' || task.priority === priorityFilter;
      const matchesProject = projectFilter === 'ALL' || task.projectId === projectFilter;
      return matchesSearch && matchesStatus && matchesPriority && matchesProject;
    });
  }, [tasks, searchTerm, statusFilter, priorityFilter, projectFilter]);

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsNewTaskModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsNewTaskModalOpen(false);
    setEditingTask(null);
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.NAO_INICIADO: return 'bg-slate-100 text-slate-700';
      case TaskStatus.EM_PROGRESSO: return 'bg-blue-100 text-blue-700';
      case TaskStatus.AGUARDANDO: return 'bg-amber-100 text-amber-700';
      case TaskStatus.CONCLUIDO: return 'bg-emerald-100 text-emerald-700';
      case TaskStatus.CANCELADO: return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.BAIXA: return 'bg-slate-100 text-slate-700';
      case Priority.MEDIA: return 'bg-blue-100 text-blue-700';
      case Priority.ALTA: return 'bg-orange-100 text-orange-700';
      case Priority.URGENTE: return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Tarefas</h1>
          <p className="text-slate-500">Acompanhe e gerencie as atividades dos seus projetos</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsNewTaskModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nova Tarefa
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <ListIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total de Tarefas</p>
              <p className="text-xl font-bold text-slate-900">{tasks.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Em Progresso</p>
              <p className="text-xl font-bold text-slate-900">
                {tasks.filter(t => t.status === TaskStatus.EM_PROGRESSO).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Concluídas</p>
              <p className="text-xl font-bold text-slate-900">
                {tasks.filter(t => t.status === TaskStatus.CONCLUIDO).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-50 text-rose-600 rounded-lg">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Urgentes</p>
              <p className="text-xl font-bold text-slate-900">
                {tasks.filter(t => t.priority === Priority.URGENTE).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar tarefas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="ALL">Todos os Status</option>
              {Object.values(TaskStatus).map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as any)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="ALL">Todas as Prioridades</option>
              {Object.values(Priority).map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              <option value="ALL">Todos os Projetos</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden lg:block" />
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <ListIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`p-1.5 rounded-md transition-all ${viewMode === 'kanban' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <Columns className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Tasks List */}
      {viewMode === 'table' ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-bottom border-slate-200">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tarefa</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Projeto</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Prioridade</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Responsáveis</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Prazo</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredTasks.map((task) => {
                  const project = projects.find(p => p.id === task.projectId);
                  return (
                    <tr key={task.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{task.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{task.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600">{project?.name || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={getStatusColor(task.status)}>{task.status}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex -space-x-2">
                          {task.assignedTo.slice(0, 3).map((userId, i) => {
                            const user = users.find(u => u.id === userId);
                            return (
                              <div 
                                key={i}
                                className="w-7 h-7 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden"
                                title={user?.name}
                              >
                                {user?.avatar ? (
                                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                                ) : (
                                  <UserIcon className="w-3 h-3 text-slate-500" />
                                )}
                              </div>
                            );
                          })}
                          {task.assignedTo.length > 3 && (
                            <div className="w-7 h-7 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-[10px] font-medium text-slate-600">
                              +{task.assignedTo.length - 3}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {task.deadline ? format(new Date(task.deadline), 'dd/MM/yyyy', { locale: ptBR }) : 'Sem prazo'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => handleEditTask(task)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => onDeleteTask(task.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTasks.map((task) => {
            const project = projects.find(p => p.id === task.projectId);
            return (
              <Card key={task.id} className="p-4 hover:border-indigo-200 transition-all group">
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <Badge className={getStatusColor(task.status)}>{task.status}</Badge>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => handleEditTask(task)}
                        className="p-1 text-slate-400 hover:text-indigo-600 rounded"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{task.name}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mt-1">{task.description}</p>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Tag className="w-3 h-3" />
                    <span>{project?.name || 'N/A'}</span>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {task.assignedTo.slice(0, 3).map((userId, i) => {
                        const user = users.find(u => u.id === userId);
                        return (
                          <div 
                            key={i}
                            className="w-7 h-7 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden"
                            title={user?.name}
                          >
                            {user?.avatar ? (
                              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                              <UserIcon className="w-3 h-3 text-slate-500" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                      <Calendar className="w-3 h-3" />
                      {task.deadline ? format(new Date(task.deadline), 'dd/MM', { locale: ptBR }) : 'S/P'}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="flex gap-6 overflow-x-auto pb-6 min-h-[600px]">
          {Object.values(TaskStatus).map(status => {
            const statusTasks = filteredTasks.filter(t => t.status === status);
            return (
              <div key={status} className="flex-shrink-0 w-80 flex flex-col gap-4">
                <div className="flex items-center justify-between px-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-700 uppercase tracking-wider text-xs">{status}</h3>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-[10px] font-bold">
                      {statusTasks.length}
                    </span>
                  </div>
                </div>
                
                <div className="flex-1 space-y-3">
                  {statusTasks.map(task => {
                    const project = projects.find(p => p.id === task.projectId);
                    return (
                      <motion.div
                        key={task.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <Card className="p-4 hover:border-indigo-200 transition-all cursor-pointer group" onClick={() => handleEditTask(task)}>
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button className="p-1 text-slate-400 hover:text-indigo-600 rounded">
                                  <Edit className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            <div>
                              <h4 className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{task.name}</h4>
                              <p className="text-xs text-slate-500 line-clamp-2 mt-1">{task.description}</p>
                            </div>

                            <div className="flex items-center gap-2 text-[10px] text-slate-400">
                              <Tag className="w-3 h-3" />
                              <span className="truncate">{project?.name || 'N/A'}</span>
                            </div>

                            <div className="pt-3 border-t border-slate-50 flex items-center justify-between">
                              <div className="flex -space-x-1.5">
                                {task.assignedTo.slice(0, 3).map((userId, i) => {
                                  const user = users.find(u => u.id === userId);
                                  return (
                                    <div 
                                      key={i}
                                      className="w-6 h-6 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden"
                                      title={user?.name}
                                    >
                                      {user?.avatar ? (
                                        <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <UserIcon className="w-2.5 h-2.5 text-slate-500" />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
                                <Calendar className="w-3 h-3" />
                                {task.deadline ? format(new Date(task.deadline), 'dd/MM', { locale: ptBR }) : 'S/P'}
                              </div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Task Modal */}
      <AnimatePresence>
        {isNewTaskModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">
                  {editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}
                </h2>
                <button 
                  onClick={handleCloseModal}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5 rotate-45" />
                </button>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const taskData = {
                  name: formData.get('name') as string,
                  description: formData.get('description') as string,
                  projectId: formData.get('projectId') as string,
                  status: formData.get('status') as TaskStatus,
                  priority: formData.get('priority') as Priority,
                  deadline: formData.get('deadline') as string,
                  assignedTo: Array.from(formData.getAll('assignedTo')) as string[],
                  tags: (formData.get('tags') as string).split(',').map(t => t.trim()).filter(t => t !== ''),
                };

                if (editingTask) {
                  onUpdateTask({ ...editingTask, ...taskData });
                } else {
                  onAddTask(taskData);
                }
                handleCloseModal();
              }} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nome da Tarefa</label>
                    <input
                      name="name"
                      required
                      defaultValue={editingTask?.name}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
                    <textarea
                      name="description"
                      rows={3}
                      defaultValue={editingTask?.description}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Projeto</label>
                    <select
                      name="projectId"
                      required
                      defaultValue={editingTask?.projectId}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    >
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prioridade</label>
                    <select
                      name="priority"
                      defaultValue={editingTask?.priority}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    >
                      {Object.values(Priority).map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select
                      name="status"
                      defaultValue={editingTask?.status}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    >
                      {Object.values(TaskStatus).map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prazo</label>
                    <input
                      name="deadline"
                      type="date"
                      defaultValue={editingTask?.deadline ? format(new Date(editingTask.deadline), 'yyyy-MM-dd') : ''}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Responsáveis</label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-slate-200 rounded-lg">
                      {users.map(u => (
                        <label key={u.id} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-indigo-600">
                          <input 
                            type="checkbox" 
                            name="assignedTo" 
                            value={u.id} 
                            defaultChecked={editingTask?.assignedTo.includes(u.id)}
                            className="rounded text-indigo-600 focus:ring-indigo-500"
                          />
                          {u.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tags (separadas por vírgula)</label>
                    <input
                      name="tags"
                      defaultValue={editingTask?.tags.join(', ')}
                      placeholder="design, frontend, bug"
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                  >
                    {editingTask ? 'Salvar Alterações' : 'Criar Tarefa'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
